import { Link } from "@tanstack/react-router";
import { Activity, FlaskConical, LineChart, Calculator, ShieldAlert, Home, HelpCircle } from "lucide-react";
import { ThemeToggle } from "./theme-toggle";
import { LanguageToggle } from "./language-toggle";
import { useI18n } from "@/lib/i18n";

export function TopNav() {
  const { t } = useI18n();
  const de = t.nav.home === "Start";
  const links = [
    { to: "/", label: t.nav.home, icon: Home },
    { to: "/wissenschaft", label: t.nav.science, icon: FlaskConical },
    { to: "/studien", label: t.nav.studies, icon: LineChart },
    { to: "/rechner", label: t.nav.calculator, icon: Calculator },
    { to: "/sicherheit", label: t.nav.safety, icon: ShieldAlert },
    { to: "/faq", label: t.nav.faq, icon: HelpCircle },
  ] as const;
  const moreLinks = [
    { to: "/vergleich" as const, label: de ? "Vergleich" : "Comparison" },
    { to: "/reta-vs-ozempic" as const, label: "Reta vs Ozempic" },
    { to: "/promis" as const, label: de ? "Promis" : "Celebrities" },
    { to: "/zulassung" as const, label: de ? "Zulassung" : "Approval" },
    { to: "/nebenwirkungen" as const, label: de ? "Nebenwirkungen" : "Side effects" },
    { to: "/thailand" as const, label: "Thailand" },
    { to: "/ueber-uns" as const, label: de ? "Über uns" : "About us" },
    { to: "/kontakt" as const, label: de ? "Kontakt" : "Contact" },
  ];



  return (
    <>
      <header className="sticky top-0 z-40 border-b border-border/60 bg-background/80 backdrop-blur-lg">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4">
          <Link to="/" className="flex items-center gap-2">
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-indigo text-indigo-foreground">
              <Activity className="h-4 w-4" />
            </span>
            <span className="font-semibold tracking-tight">
              Retatrutid<span className="text-indigo"> Thailand</span>
            </span>
          </Link>
          <nav className="hidden items-center gap-1 md:flex">
            {links.slice(1).map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                className="rounded-md px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                activeProps={{ className: "rounded-md px-3 py-1.5 text-sm text-foreground bg-accent" }}
              >
                {label}
              </Link>
            ))}
            {moreLinks.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                className="hidden rounded-md px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground lg:inline-flex"
                activeProps={{ className: "hidden rounded-md px-3 py-1.5 text-sm text-foreground bg-accent lg:inline-flex" }}
              >
                {label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <LanguageToggle />
            <ThemeToggle />
          </div>
        </div>
      </header>

      <div className="pointer-events-none fixed bottom-[calc(env(safe-area-inset-bottom)+16px)] right-3 z-50 md:hidden">
        <div className="pointer-events-auto">
          <LanguageToggle />
        </div>
      </div>

      <nav
        aria-label="Mobile navigation"
        className="sticky top-16 z-30 border-b border-border/60 bg-background/90 backdrop-blur-lg md:hidden"
      >
        <ul className="mx-auto grid max-w-md grid-cols-6">
          {links.map(({ to, label, icon: Icon }) => (
            <li key={to}>
              <Link
                to={to}
                className="flex flex-col items-center gap-1 py-2.5 text-[11px] font-medium text-muted-foreground transition-colors"
                activeProps={{
                  className: "flex flex-col items-center gap-1 py-2.5 text-[11px] font-medium text-indigo",
                }}
                activeOptions={{ exact: to === "/" }}
              >
                <Icon className="h-5 w-5" />
                {label}
              </Link>
            </li>
          ))}
        </ul>
      </nav>
    </>
  );
}

// kept for compatibility (BottomNav is now rendered inside TopNav)
export function BottomNav() {
  return null;
}
